import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ChevronLeft, ChevronRight, Play, Pause, Users } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface User {
  id: string;
  username: string;
  annotation_count: number;
}

interface Annotation {
  annotation_id: string;
  sample_id: string;
  user_rating: string;
  reaction_time_ms: number;
  sample_time_ms: number;
  created_at: string;
  dimension: string;
  level: string;
  polarity: string;
  file_name: string;
  file_path: string;
}

interface UserAnnotations {
  user: {
    id: string;
    username: string;
  };
  annotations: Annotation[];
  total: number;
}

export function ExamplesViewer() {
  const [token, setToken] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [userAnnotations, setUserAnnotations] = useState<UserAnnotations | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef] = useState(new Audio());

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    if (urlToken) {
      setToken(urlToken);
      fetchUsers(urlToken);
    }
  }, []);

  useEffect(() => {
    return () => {
      audioRef.pause();
      audioRef.src = '';
    };
  }, [audioRef]);

  const fetchUsers = async (authToken: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/examples?token=${authToken}`);
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      setUsers(data.users);
    } catch (err) {
      setError('Failed to load users. Please check your token.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAnnotations = async (userId: string) => {
    setLoading(true);
    setError('');
    setCurrentIndex(0);
    audioRef.pause();
    setIsPlaying(false);
    
    try {
      const response = await fetch(`/api/examples/${userId}?token=${token}`);
      if (!response.ok) {
        throw new Error('Failed to fetch annotations');
      }
      const data = await response.json();
      setUserAnnotations(data);
    } catch (err) {
      setError('Failed to load annotations.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    fetchUserAnnotations(userId);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      audioRef.pause();
      setIsPlaying(false);
    }
  };

  const handleNext = () => {
    if (userAnnotations && currentIndex < userAnnotations.annotations.length - 1) {
      setCurrentIndex(currentIndex + 1);
      audioRef.pause();
      setIsPlaying(false);
    }
  };

  // Auto-play audio when currentIndex changes
  useEffect(() => {
    if (!userAnnotations || !token) return;
    
    const annotation = userAnnotations.annotations[currentIndex];
    if (!annotation) return;

    const audioUrl = `/api/public-audio/${annotation.sample_id}?token=${token}`;
    
    audioRef.pause();
    audioRef.src = audioUrl;
    audioRef.load();
    
    // Auto-play after a short delay to ensure the audio is loaded
    const playPromise = audioRef.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true);
        })
        .catch((error) => {
          // Auto-play was prevented, user needs to click play
          console.log('Auto-play prevented:', error);
          setIsPlaying(false);
        });
    }
  }, [currentIndex, userAnnotations, token, audioRef]);

  const togglePlayPause = () => {
    if (!userAnnotations) return;
    
    const annotation = userAnnotations.annotations[currentIndex];
    if (!annotation) return;

    // Construct the proper audio URL using the public API endpoint with token
    const audioUrl = `/api/public-audio/${annotation.sample_id}?token=${token}`;

    if (isPlaying) {
      audioRef.pause();
      setIsPlaying(false);
    } else {
      if (audioRef.src !== audioUrl) {
        audioRef.src = audioUrl;
      }
      audioRef.play();
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    const handleEnded = () => setIsPlaying(false);
    audioRef.addEventListener('ended', handleEnded);
    return () => audioRef.removeEventListener('ended', handleEnded);
  }, [audioRef]);

  const getRatingBadge = (rating: string) => {
    const variants: Record<string, { label: string; className: string }> = {
      yes: { label: 'Yes', className: 'bg-green-500' },
      no: { label: 'No', className: 'bg-red-500' },
    };
    const config = variants[rating] || { label: rating, className: '' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (!token) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Access Required</CardTitle>
            <CardDescription>Please provide a valid token in the URL</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Example: /examples?token=YOUR_TOKEN
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentAnnotation = userAnnotations?.annotations[currentIndex];

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <Users className="h-6 w-6" />
        <h1 className="text-3xl font-bold">Annotation Examples</h1>
      </div>

      {error && (
        <Card className="border-red-500">
          <CardContent className="pt-6">
            <p className="text-red-500">{error}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Select User</CardTitle>
          <CardDescription>Choose a user to view their annotations</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedUserId} onValueChange={handleUserSelect} disabled={loading}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a user..." />
            </SelectTrigger>
            <SelectContent>
              {users.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.username} ({user.annotation_count} annotations)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center">Loading...</p>
          </CardContent>
        </Card>
      )}

      {userAnnotations && currentAnnotation && (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>
                    {userAnnotations.user.username} - Example {currentIndex + 1} of {userAnnotations.total}
                  </CardTitle>
                  <CardDescription>{currentAnnotation.file_name}</CardDescription>
                </div>
                <Button onClick={togglePlayPause} size="lg">
                  {isPlaying ? <Pause className="h-5 w-5 mr-2" /> : <Play className="h-5 w-5 mr-2" />}
                  {isPlaying ? 'Pause' : 'Play Audio'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Dimension:</span>
                    <Badge variant="outline" className="text-base">
                      {currentAnnotation.dimension}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Level:</span>
                    <Badge variant="secondary">{currentAnnotation.level}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Polarity:</span>
                    <Badge variant="secondary">{currentAnnotation.polarity}</Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">User Rating:</span>
                    {getRatingBadge(currentAnnotation.user_rating)}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Reaction Time:</span>
                    <span className="text-sm">{(currentAnnotation.reaction_time_ms / 1000).toFixed(2)}s</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Sample Duration:</span>
                    <span className="text-sm">{(currentAnnotation.sample_time_ms / 1000).toFixed(2)}s</span>
                  </div>
                </div>
              </div>

              <div className="text-xs text-muted-foreground">
                Annotated: {new Date(currentAnnotation.created_at).toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between items-center">
            <Button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              variant="outline"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              {currentIndex + 1} / {userAnnotations.total}
            </span>
            <Button
              onClick={handleNext}
              disabled={currentIndex === userAnnotations.annotations.length - 1}
              variant="outline"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </>
      )}

      {userAnnotations && userAnnotations.annotations.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">No annotations found for this user.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
